import React, { useEffect, useMemo, useState } from "react";
import TopBar from "../components/TopBar";
import { Field } from "./Fields";
import { api, fmtUSD, fmtNum } from "../lib/api";
import { useYear } from "../context/YearContext";
import AddableSelect from "../components/AddableSelect";
import { Save, Wand2, Trash2, Plus, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const SPECIES = ["Cattle", "Horse", "Sheep", "Goat", "Pig", "Chicken", "Other"];
const OTHER_CATS = [
  { value: "custom_farming", label: "Custom Farming / Hired Out" },
  { value: "govt_program", label: "Government Programs (FSA, ARC/PLC, conservation)" },
  { value: "hay_forage", label: "Hay / Forage Sales" },
  { value: "equipment_rental", label: "Equipment Rental Income" },
  { value: "crop_insurance", label: "Crop Insurance Proceeds" },
  { value: "other", label: "Other" },
];

export default function Projections() {
  const { year, years } = useYear();
  const [proj, setProj] = useState(null);
  const [summary, setSummary] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fillFromYear, setFillFromYear] = useState("");

  const load = async () => {
    const [p, s] = await Promise.all([
      api.get(`/projections/${year}`),
      api.get(`/projections/${year}/summary`),
    ]);
    setProj(p.data);
    setSummary(s.data);
  };

  useEffect(() => {
    load();
  }, [year]);

  // Default the prefill picker to the most-recent prior year that exists
  useEffect(() => {
    if (years && years.length) {
      const prior = years.filter((y) => y < year);
      if (prior.length) setFillFromYear(prior[0]);
    }
  }, [years, year]);

  const onAutoFill = async () => {
    if (!fillFromYear) {
      toast.error("Pick a year to copy from");
      return;
    }
    if (!window.confirm(`Replace current projection with ${fillFromYear} actuals as a starting point?`)) return;
    const { data } = await api.get(`/projections/${year}/template?from_year=${fillFromYear}`);
    setProj(data);
    toast.success(`Filled from ${fillFromYear} — review each line and save`);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        fields: proj.fields.map((f) => ({
          ...f,
          acreage: parseFloat(f.acreage) || 0,
          yield_per_acre: parseFloat(f.yield_per_acre) || 0,
          price_per_unit: parseFloat(f.price_per_unit) || 0,
        })),
        livestock: proj.livestock.map((l) => ({
          ...l,
          head_to_sell: parseFloat(l.head_to_sell) || 0,
          price_per_head: parseFloat(l.price_per_head) || 0,
        })),
        other_income: proj.other_income.map((o) => ({
          ...o,
          amount: parseFloat(o.amount) || 0,
        })),
        estimated_expenses: parseFloat(proj.estimated_expenses) || 0,
        estimated_mileage: parseFloat(proj.estimated_mileage) || 0,
        notes: proj.notes || "",
      };
      await api.put(`/projections/${year}`, payload);
      toast.success("Projection saved");
      load();
    } catch {
      toast.error("Could not save projection");
    } finally {
      setSaving(false);
    }
  };

  // ----- live computed totals (don't require save to see) -----
  const totals = useMemo(() => {
    if (!proj) return { field: 0, livestock: 0, other: 0, revenue: 0, mileage: 0, expenses: 0, net: 0 };
    const field = proj.fields.reduce(
      (s, f) => s + (parseFloat(f.acreage) || 0) * (parseFloat(f.yield_per_acre) || 0) * (parseFloat(f.price_per_unit) || 0),
      0,
    );
    const livestock = proj.livestock.reduce(
      (s, l) => s + (parseFloat(l.head_to_sell) || 0) * (parseFloat(l.price_per_head) || 0),
      0,
    );
    const other = proj.other_income.reduce((s, o) => s + (parseFloat(o.amount) || 0), 0);
    const revenue = field + livestock + other;
    const mRate = (summary && summary.mileage_rate) || 0.67;
    const mileage = (parseFloat(proj.estimated_mileage) || 0) * mRate;
    const expenses = (parseFloat(proj.estimated_expenses) || 0) + mileage;
    return { field, livestock, other, revenue, mileage, expenses, net: revenue - expenses, mileage_rate: mRate };
  }, [proj, summary]);

  if (!proj) return <div><TopBar title="Income Projection" subtitle="Loading…" /></div>;

  const updateField = (idx, patch) => setProj({ ...proj, fields: proj.fields.map((f, i) => i === idx ? { ...f, ...patch } : f) });
  const removeField = (idx) => setProj({ ...proj, fields: proj.fields.filter((_, i) => i !== idx) });
  const addField = () => setProj({ ...proj, fields: [...proj.fields, { field_id: "", field_name: "New field", crop: "", acreage: 0, yield_per_acre: 0, unit: "bushels", price_per_unit: 0 }] });

  const updateLivestock = (idx, patch) => setProj({ ...proj, livestock: proj.livestock.map((l, i) => i === idx ? { ...l, ...patch } : l) });
  const removeLivestock = (idx) => setProj({ ...proj, livestock: proj.livestock.filter((_, i) => i !== idx) });
  const addLivestock = () => setProj({ ...proj, livestock: [...proj.livestock, { species: "Cattle", head_to_sell: 0, price_per_head: 0, notes: "" }] });

  const updateOther = (idx, patch) => setProj({ ...proj, other_income: proj.other_income.map((o, i) => i === idx ? { ...o, ...patch } : o) });
  const removeOther = (idx) => setProj({ ...proj, other_income: proj.other_income.filter((_, i) => i !== idx) });
  const addOther = () => setProj({ ...proj, other_income: [...proj.other_income, { category: "custom_farming", description: "", amount: 0 }] });

  const priorYears = (years || []).filter((y) => y < year);

  return (
    <div>
      <TopBar
        title="Income Projection"
        subtitle={`What you think you'll make in ${year}. Edit any line — totals update live.`}
        actions={
          <div className="flex flex-wrap items-center gap-2 mt-6">
            <div className="flex items-center gap-1.5 border border-[#1F1E1B] bg-white px-2 py-1.5">
              <span className="text-[10px] uppercase tracking-wider font-bold text-[#66645E]">Pre-fill from</span>
              <select data-testid="prefill-year-select" value={fillFromYear} onChange={(e) => setFillFromYear(e.target.value)} className="text-sm font-bold bg-transparent focus:outline-none">
                {priorYears.length === 0 && <option value="">— no prior year —</option>}
                {priorYears.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              <button data-testid="prefill-btn" onClick={onAutoFill} disabled={!fillFromYear} className="bg-[#1F1E1B] text-white px-3 py-1 text-xs font-bold uppercase tracking-wider hover:bg-[#2A2924] flex items-center gap-1 disabled:opacity-40">
                <Wand2 className="w-3 h-3" /> Auto-fill
              </button>
            </div>
            <button data-testid="save-projection-btn" onClick={save} disabled={saving} className="bg-[#1A4135] text-white px-5 py-2 font-bold uppercase tracking-wider text-sm hover:bg-[#133128] flex items-center gap-2 disabled:opacity-50">
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Projection"}
            </button>
          </div>
        }
      />

      {/* Live totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="projection-totals">
        <Card label="Projected Revenue" value={totals.revenue} tone="positive" />
        <Card label="Estimated Expenses" value={-totals.expenses} tone="negative" />
        <Card label="Estimated Net Income" value={totals.net} tone={totals.net >= 0 ? "highlight" : "negative"} testid="projection-net-card" />
        <Card label="Mileage Deduction" value={totals.mileage} tone="muted" hint={`${fmtNum(parseFloat(proj.estimated_mileage) || 0)} mi × $${totals.mileage_rate || 0.67}`} />
      </div>

      {/* Field projection */}
      <Section title="Crop Yield Projection" icon={TrendingUp} action={<button data-testid="add-field-projection-btn" onClick={addField} className="text-xs font-bold uppercase tracking-wider border border-[#1F1E1B] px-3 py-1 hover:bg-[#1F1E1B] hover:text-white flex items-center gap-1"><Plus className="w-3 h-3" /> Add Field</button>}>
        {proj.fields.length === 0 ? (
          <Empty>No fields. Add fields under Fields tab, or click Add Field above.</Empty>
        ) : (
          <div className="bg-white border border-[#1F1E1B] overflow-x-auto">
            <table className="ledger-table" data-testid="field-projection-table">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Crop</th>
                  <th className="num">Acres</th>
                  <th className="num">Yield/Acre</th>
                  <th>Unit</th>
                  <th className="num">Price/Unit</th>
                  <th className="num">Projected Revenue</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {proj.fields.map((f, i) => {
                  const rev = (parseFloat(f.acreage) || 0) * (parseFloat(f.yield_per_acre) || 0) * (parseFloat(f.price_per_unit) || 0);
                  return (
                    <tr key={i} data-testid={`field-projection-row-${i}`}>
                      <td><input data-testid={`fp-name-${i}`} value={f.field_name || ""} onChange={(e) => updateField(i, { field_name: e.target.value })} className="w-full bg-transparent border-b border-dashed border-[#D2CEC1] focus:outline-none focus:border-[#1F1E1B]" /></td>
                      <td><input data-testid={`fp-crop-${i}`} value={f.crop || ""} onChange={(e) => updateField(i, { crop: e.target.value })} className="w-full bg-transparent border-b border-dashed border-[#D2CEC1] focus:outline-none focus:border-[#1F1E1B]" /></td>
                      <td><input data-testid={`fp-acreage-${i}`} type="number" step="0.01" value={f.acreage} onChange={(e) => updateField(i, { acreage: e.target.value })} className="w-24 text-right bg-transparent border-b border-dashed border-[#D2CEC1] focus:outline-none focus:border-[#1F1E1B] tabular" /></td>
                      <td><input data-testid={`fp-yield-${i}`} type="number" step="0.01" value={f.yield_per_acre} onChange={(e) => updateField(i, { yield_per_acre: e.target.value })} className="w-24 text-right bg-transparent border-b border-dashed border-[#D2CEC1] focus:outline-none focus:border-[#1F1E1B] tabular" /></td>
                      <td><input data-testid={`fp-unit-${i}`} value={f.unit || "bushels"} onChange={(e) => updateField(i, { unit: e.target.value })} className="w-24 bg-transparent border-b border-dashed border-[#D2CEC1] focus:outline-none focus:border-[#1F1E1B]" /></td>
                      <td><input data-testid={`fp-price-${i}`} type="number" step="0.01" value={f.price_per_unit} onChange={(e) => updateField(i, { price_per_unit: e.target.value })} className="w-24 text-right bg-transparent border-b border-dashed border-[#D2CEC1] focus:outline-none focus:border-[#1F1E1B] tabular" /></td>
                      <td className="num font-bold">{fmtUSD(rev)}</td>
                      <td><button data-testid={`remove-fp-${i}`} onClick={() => removeField(i)} className="p-1 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white"><Trash2 className="w-3 h-3" /></button></td>
                    </tr>
                  );
                })}
                <tr className="bg-[#F5F4F0]">
                  <td colSpan={6} className="font-bold uppercase tracking-wider text-xs">Subtotal — Crop Revenue</td>
                  <td className="num font-display text-lg font-black">{fmtUSD(totals.field)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Livestock */}
      <Section title="Livestock Sales Projection" action={<button data-testid="add-livestock-projection-btn" onClick={addLivestock} className="text-xs font-bold uppercase tracking-wider border border-[#1F1E1B] px-3 py-1 hover:bg-[#1F1E1B] hover:text-white flex items-center gap-1"><Plus className="w-3 h-3" /> Add Species</button>}>
        {proj.livestock.length === 0 ? (
          <Empty>No livestock projection rows. Click Add Species above.</Empty>
        ) : (
          <div className="bg-white border border-[#1F1E1B] overflow-x-auto">
            <table className="ledger-table" data-testid="livestock-projection-table">
              <thead>
                <tr>
                  <th>Species</th>
                  <th className="num">Head to Sell</th>
                  <th className="num">Price / Head</th>
                  <th>Notes</th>
                  <th className="num">Projected Revenue</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {proj.livestock.map((l, i) => {
                  const rev = (parseFloat(l.head_to_sell) || 0) * (parseFloat(l.price_per_head) || 0);
                  return (
                    <tr key={i} data-testid={`livestock-projection-row-${i}`}>
                      <td>
                        <AddableSelect
                          domain="species"
                          defaults={SPECIES}
                          value={l.species}
                          onChange={(v) => updateLivestock(i, { species: v })}
                          testid={`lp-species-${i}`}
                          className="w-full bg-transparent border-b border-dashed border-[#D2CEC1] focus:outline-none focus:border-[#1F1E1B]"
                        />
                      </td>
                      <td><input data-testid={`lp-head-${i}`} type="number" step="0.01" value={l.head_to_sell} onChange={(e) => updateLivestock(i, { head_to_sell: e.target.value })} className="w-24 text-right bg-transparent border-b border-dashed border-[#D2CEC1] focus:outline-none focus:border-[#1F1E1B] tabular" /></td>
                      <td><input data-testid={`lp-price-${i}`} type="number" step="0.01" value={l.price_per_head} onChange={(e) => updateLivestock(i, { price_per_head: e.target.value })} className="w-28 text-right bg-transparent border-b border-dashed border-[#D2CEC1] focus:outline-none focus:border-[#1F1E1B] tabular" /></td>
                      <td><input data-testid={`lp-notes-${i}`} value={l.notes || ""} onChange={(e) => updateLivestock(i, { notes: e.target.value })} className="w-full bg-transparent border-b border-dashed border-[#D2CEC1] focus:outline-none focus:border-[#1F1E1B]" /></td>
                      <td className="num font-bold">{fmtUSD(rev)}</td>
                      <td><button data-testid={`remove-lp-${i}`} onClick={() => removeLivestock(i)} className="p-1 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white"><Trash2 className="w-3 h-3" /></button></td>
                    </tr>
                  );
                })}
                <tr className="bg-[#F5F4F0]">
                  <td colSpan={4} className="font-bold uppercase tracking-wider text-xs">Subtotal — Livestock Revenue</td>
                  <td className="num font-display text-lg font-black">{fmtUSD(totals.livestock)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Other income */}
      <Section title="Other Farm Income" action={<button data-testid="add-other-income-btn" onClick={addOther} className="text-xs font-bold uppercase tracking-wider border border-[#1F1E1B] px-3 py-1 hover:bg-[#1F1E1B] hover:text-white flex items-center gap-1"><Plus className="w-3 h-3" /> Add Income Source</button>}>
        {proj.other_income.length === 0 ? (
          <Empty>No other income projected.</Empty>
        ) : (
          <div className="bg-white border border-[#1F1E1B] overflow-x-auto">
            <table className="ledger-table" data-testid="other-income-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Description</th>
                  <th className="num">Projected Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {proj.other_income.map((o, i) => (
                  <tr key={i} data-testid={`other-income-row-${i}`}>
                    <td>
                      <AddableSelect
                        domain="income_category"
                        defaults={OTHER_CATS}
                        value={o.category}
                        onChange={(v) => updateOther(i, { category: v })}
                        testid={`oi-cat-${i}`}
                        className="w-full bg-transparent border-b border-dashed border-[#D2CEC1] focus:outline-none focus:border-[#1F1E1B]"
                      />
                    </td>
                    <td><input data-testid={`oi-desc-${i}`} value={o.description || ""} onChange={(e) => updateOther(i, { description: e.target.value })} placeholder="e.g. Spring planting for J. Smith" className="w-full bg-transparent border-b border-dashed border-[#D2CEC1] focus:outline-none focus:border-[#1F1E1B]" /></td>
                    <td><input data-testid={`oi-amount-${i}`} type="number" step="0.01" value={o.amount} onChange={(e) => updateOther(i, { amount: e.target.value })} className="w-32 text-right bg-transparent border-b border-dashed border-[#D2CEC1] focus:outline-none focus:border-[#1F1E1B] tabular" /></td>
                    <td><button data-testid={`remove-oi-${i}`} onClick={() => removeOther(i)} className="p-1 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white"><Trash2 className="w-3 h-3" /></button></td>
                  </tr>
                ))}
                <tr className="bg-[#F5F4F0]">
                  <td colSpan={2} className="font-bold uppercase tracking-wider text-xs">Subtotal — Other Income</td>
                  <td className="num font-display text-lg font-black">{fmtUSD(totals.other)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Cost side */}
      <Section title="Estimated Costs">
        <div className="bg-white border border-[#1F1E1B] p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Estimated Total Expenses (USD)">
            <input
              data-testid="est-expenses-input"
              type="number"
              step="0.01"
              value={proj.estimated_expenses}
              onChange={(e) => setProj({ ...proj, estimated_expenses: e.target.value })}
              className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular"
              placeholder="Use last year's total as a starting point"
            />
          </Field>
          <Field label="Estimated Business Miles">
            <input
              data-testid="est-mileage-input"
              type="number"
              step="0.01"
              value={proj.estimated_mileage}
              onChange={(e) => setProj({ ...proj, estimated_mileage: e.target.value })}
              className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular"
              placeholder="Total miles you expect to drive for the farm"
            />
            <div className="text-[11px] text-[#66645E] mt-1">Mileage deduction: {fmtUSD(totals.mileage)} ({fmtNum(parseFloat(proj.estimated_mileage) || 0)} mi × ${totals.mileage_rate || 0.67})</div>
          </Field>
        </div>
      </Section>

      {/* Notes */}
      <Section title="Notes">
        <textarea
          data-testid="projection-notes"
          rows={3}
          value={proj.notes || ""}
          onChange={(e) => setProj({ ...proj, notes: e.target.value })}
          placeholder="Any assumptions worth remembering — weather, prices held flat, etc."
          className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm"
        />
      </Section>

      {/* Bottom net */}
      <div className="bg-white border-2 border-[#1F1E1B] p-6 mt-6 flex items-baseline justify-between" data-testid="projection-bottom-net">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Estimated Net Farm Income — {year}</div>
          <div className="text-sm text-[#66645E] mt-1">Projected revenue minus estimated expenses minus mileage deduction.</div>
        </div>
        <div className={`font-display text-5xl font-black tabular ${totals.net >= 0 ? "text-[#1A4135]" : "text-[#BC4727]"}`}>
          {fmtUSD(totals.net)}
        </div>
      </div>
    </div>
  );
}

function Card({ label, value, tone, hint, testid }) {
  const color = tone === "positive" ? "text-[#1A4135]" : tone === "negative" ? "text-[#BC4727]" : tone === "muted" ? "text-[#66645E]" : "text-[#1F1E1B]";
  return (
    <div className="border border-[#1F1E1B] p-4 bg-white" data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">{label}</div>
      <div className={`font-display text-3xl font-black tabular mt-1 ${color}`}>{fmtUSD(value)}</div>
      {hint && <div className="text-[10px] text-[#66645E] mt-1">{hint}</div>}
    </div>
  );
}

function Section({ title, icon: Icon, action, children }) {
  return (
    <div className="mb-6">
      <div className="flex items-baseline justify-between border-b border-[#1F1E1B] pb-2 mb-3">
        <h3 className="font-display text-lg font-bold uppercase tracking-wider flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4" />} {title}
        </h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <div className="border border-dashed border-[#D2CEC1] p-6 text-center text-sm text-[#66645E] italic bg-white">{children}</div>;
}
